import type { Task } from "../tasks/types.js";
import type { Label } from "../labels/types.js";
import type { Topic } from "../topics/types.js";
import type { Calendar } from "../calendars/types.js";
import { reconcileIncoming, remapIds } from "../dedup/index.js";
import type { BackupEnvelope } from "./types.js";

export interface ExistingIds {
  taskIds: Set<string>;
  ruleIds: Set<string>;
  overrideIds: Set<string>;
  /** The full existing rows (not just ids) so incoming labels/topics/calendars
   *  can be deduped by NAME, not only by id — a backup carrying a "Work" topic
   *  with a different id than the local one must NOT create a second "Work". */
  labels: Label[];
  topics: Topic[];
  calendars: Calendar[];
}

export interface MergeInput {
  existing: ExistingIds;
  incoming: BackupEnvelope;
}

/**
 * Dedup-by-id-AND-name merge planner. Returns ONLY the rows to insert, never a
 * dangling or duplicate reference:
 *  - labels/topics/calendars colliding with an existing one by NAME are dropped
 *    and every incoming task's references are remapped to the existing id (so an
 *    import can't double "Work");
 *  - a task pointing at an absent rule is degraded to one-shot;
 *  - an override whose parent rule is absent is dropped.
 * Pure — no I/O.
 */
export function planMerge({ existing, incoming }: MergeInput): BackupEnvelope {
  const labelRec = reconcileIncoming(existing.labels, incoming.labels);
  const topicRec = reconcileIncoming(existing.topics, incoming.topics ?? []);
  const calRec = reconcileIncoming(existing.calendars, incoming.calendars ?? []);

  const rules = incoming.recurrenceRules.filter((r) => !existing.ruleIds.has(r.id));
  const resultingRuleIds = new Set(existing.ruleIds);
  for (const r of rules) resultingRuleIds.add(r.id);

  const tasks = incoming.tasks
    .filter((t) => !existing.taskIds.has(t.id))
    .map((t) => degradeOrphanTask(t, resultingRuleIds))
    .map((t) => remapTaskRefs(t, labelRec.remap, topicRec.remap, calRec.remap));

  const recurrenceOverrides = incoming.recurrenceOverrides.filter(
    (o) => !existing.overrideIds.has(o.id) && resultingRuleIds.has(o.recurrence_id),
  );

  return {
    version: incoming.version,
    exportedAt: incoming.exportedAt,
    tasks,
    recurrenceRules: rules,
    recurrenceOverrides,
    labels: labelRec.keep,
    topics: topicRec.keep,
    calendars: calRec.keep,
    ...(incoming.themes ? { themes: incoming.themes } : {}),
    ...(incoming.preferences ? { preferences: incoming.preferences } : {}),
  };
}

function degradeOrphanTask(t: Task, ruleIds: Set<string>): Task {
  if (t.recurrence_id && !ruleIds.has(t.recurrence_id)) {
    return { ...t, recurrence_id: null };
  }
  return t;
}

function remapTaskRefs(
  t: Task,
  labelRemap: Map<string, string>,
  topicRemap: Map<string, string>,
  calRemap: Map<string, string>,
): Task {
  const label_ids = remapIds(t.label_ids, labelRemap);
  const topic_ids = remapIds(t.topic_ids, topicRemap);
  const calendar_id = t.calendar_id ? calRemap.get(t.calendar_id) ?? t.calendar_id : t.calendar_id;
  if (label_ids === t.label_ids && topic_ids === t.topic_ids && calendar_id === t.calendar_id) {
    return t;
  }
  return { ...t, label_ids, topic_ids, calendar_id };
}
